/**
 * provider.ts — Multi-wallet provider resolver
 *
 * Problem: Microsoft Edge injects `evmAsk.js` which proxies ALL window.ethereum
 * calls through its wallet arbitration engine (selectExtension). When MetaMask
 * and Edge Wallet are both installed, selectExtension throws "Unexpected error"
 * on every request, breaking wallet connect entirely.
 *
 * Solution: Use EIP-5749 window.ethereum.providers[] to resolve the MetaMask
 * provider directly, bypassing Edge's proxy completely.
 */

type EthereumProvider = {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  on: (event: string, handler: (...args: any[]) => void) => void;
  removeListener: (event: string, handler: (...args: any[]) => void) => void;
  isMetaMask?: boolean;
  isEdgeWallet?: boolean;
  isBraveWallet?: boolean;
  isCoinbaseWallet?: boolean;
  providers?: EthereumProvider[];
};

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

/**
 * Returns the best available Ethereum provider.
 *
 * Priority:
 * 1. MetaMask (is isMetaMask && !isEdgeWallet) from providers[]
 * 2. Any non-Edge, non-Brave provider from providers[]
 * 3. First provider from providers[] 
 * 4. window.ethereum directly (single-wallet scenario)
 *
 * This bypasses Edge's evmAsk.js wallet arbitration proxy which crashes when
 * multiple wallets are installed.
 */
export function getPreferredProvider(): EthereumProvider | null {
  if (typeof window === 'undefined' || !window.ethereum) return null;

  // EIP-5749: Multiple wallets detected — providers[] is populated
  const providers = window.ethereum.providers;
  if (Array.isArray(providers) && providers.length > 0) {
    // Prefer MetaMask that is NOT the Edge Wallet shim
    const metamask = providers.find(p => p.isMetaMask && !p.isEdgeWallet);
    if (metamask) return metamask;

    // Fall back to any non-Edge, non-Brave provider
    const nonEdge = providers.find(p => !p.isEdgeWallet && !p.isBraveWallet);
    if (nonEdge) return nonEdge;

    // Last resort: first provider
    return providers[0];
  }

  return window.ethereum;
}

/**
 * Returns true if any Ethereum provider is available.
 * Use this instead of `!!window.ethereum` to handle Edge's proxy gracefully.
 */
export function hasEthereumProvider(): boolean {
  return getPreferredProvider() !== null;
}
